import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import {
  CrmActionItem,
  CrmAirCarrierLicence,
  CrmClientDetailsData,
  CrmClientNetwork,
  CrmClientContactOption,
  CrmClientReferral,
  CrmClientReferralOption,
  CrmCompanyNetworkLink,
  CrmContact,
  CrmContactPerson,
  CrmDashboardData,
  CrmDropdownOptions,
  CrmDuplicateCandidate,
  CrmLcDetailsData,
  CrmNetwork,
  CrmQuickBooksInvoicePlaceholder,
  CrmSupportTicketPlaceholder
} from './crm.types';

export interface CrmClientResponse {
  client: CrmNetwork;
}

export interface CrmClientContactsResponse {
  company_id: number;
  contacts: CrmContact[];
}

export interface CrmClientNetworksResponse {
  company_id: number;
  primary_network: CrmCompanyNetworkLink | null;
  linked_networks: CrmCompanyNetworkLink[];
}

export interface CrmClientReferralOptionsResponse {
  options: CrmClientReferralOption[];
}

export interface CrmClientContactOptionsResponse {
  options: CrmClientContactOption[];
}

export interface CrmClientMutationResponse extends CrmClientResponse {
  success: boolean;
  message: string;
  referral?: CrmClientReferral | null;
}

export interface CrmClientContactMutationResponse {
  success: boolean;
  message: string;
  contact: CrmContact;
  contacts: CrmContactPerson[];
}

export interface CrmClientLogMutationResponse {
  success: boolean;
  message: string;
}

export interface CrmDropdownMutationResponse {
  success: boolean;
  message: string;
  dropdowns: CrmDropdownOptions;
}

export interface CrmClientActionMutationResponse {
  success: boolean;
  message: string;
  action: CrmActionItem;
  actions: CrmActionItem[];
}

export interface CrmCurrentUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface CrmLoginResponse {
  success: boolean;
  message: string;
  user: CrmCurrentUser;
}

export type CrmClientPayload = Partial<CrmNetwork> | Record<string, unknown>;

const CRM_CURRENT_USER_STORAGE_KEY = 'crm.currentUser';
const EMPTY_CRM_DROPDOWNS: CrmDropdownOptions = {
  source: [],
  industry: [],
  client_tags: [],
  user_tags: []
};

@Injectable()
export class CrmService {
  readonly crmLoginUrl: string = '/crm/crmLogin.json';
  readonly getAllClientsUrl: string = '/crm/getAllClients.json';
  readonly getAllClientsOptionsUrl: string = '/crm/getAllClientsOptions.json';
  readonly getAllClientsRecordDetailsUrl: string = '/crm/getAllClientsRecordDetails.json';
  readonly getAllClientsActionsUrl: string = '/crm/getAllClientsActions.json';
  readonly getAllClientsVendorsUrl: string = '/crm/getAllClientsVendors.json';
  readonly getAllClientsEnrichmentUrl: string = '/crm/getAllClientsEnrichment.json';
  readonly getClientUrl: string = '/crm/getClient';
  readonly getClientDetailsUrl: string = '/crm/getClientDetails';
  readonly getClientDetailsEnrichmentUrl: string = '/crm/getClientDetailsEnrichment';
  readonly getClientDetailsLcDataUrl: string = '/crm/getClientDetailsLcData';
  readonly getClientDetailsMetricsUrl: string = '/crm/getClientDetailsMetrics';
  readonly getClientDetailsUsersUrl: string = '/crm/getClientDetailsUsers';
  readonly getClientDetailsVendorLcsUrl: string = '/crm/getClientDetailsVendorLcs';
  readonly getClientDetailsByClientUrl: string = '/crm/getClientDetailsByClient';
  readonly getClientDetailsByClientEnrichmentUrl: string = '/crm/getClientDetailsByClientEnrichment';
  readonly getClientDetailsByClientLcDataUrl: string = '/crm/getClientDetailsByClientLcData';
  readonly getClientDetailsByClientMetricsUrl: string = '/crm/getClientDetailsByClientMetrics';
  readonly getClientDetailsByClientUsersUrl: string = '/crm/getClientDetailsByClientUsers';
  readonly getClientDetailsByClientVendorLcsUrl: string = '/crm/getClientDetailsByClientVendorLcs';
  readonly getClientContactsUrl: string = '/crm/getClientContacts';
  readonly getClientNetworksUrl: string = '/crm/getClientNetworks';
  readonly getClientReferralOptionsUrl: string = '/crm/getClientReferralOptions';
  readonly getClientContactOptionsUrl: string = '/crm/getClientContactOptions';
  readonly getLcDetailsUrl: string = '/crm/getLcDetails';
  readonly createClientUrl: string = '/crm/createClient.json';
  readonly createClientContactUrl: string = '/crm/createClientContact';
  readonly createClientNoteUrl: string = '/crm/createClientNote';
  readonly updateClientNoteUrl: string = '/crm/updateClientNote';
  readonly createClientActivityUrl: string = '/crm/createClientActivity';
  readonly createClientActionUrl: string = '/crm/createClientAction';
  readonly completeClientActionUrl: string = '/crm/completeClientAction';
  readonly disregardClientActionUrl: string = '/crm/disregardClientAction';
  readonly updateClientActionUrl: string = '/crm/updateClientAction';
  readonly updateClientUrl: string = '/crm/updateClient';
  readonly deleteDropdownItemUrl: string = '/crm/deleteDropdownItem';
  readonly airCarrierLicencesUrl: string = '/crm/getAirCarrierLicences.json';

  private dashboardSnapshot: CrmDashboardData | null = null;
  private dashboardRequest$: Observable<CrmDashboardData> | null = null;
  private clientDetailsSnapshots = new Map<string, CrmClientDetailsData>();
  private lcDetailsSnapshots = new Map<string, CrmLcDetailsData>();

  constructor(private http: HttpClient) { }

  loginCrmUser(username: string, password: string): Observable<CrmCurrentUser> {
    return this.http.post<CrmLoginResponse>(this.crmLoginUrl, { username, password }).pipe(
      map((response) => {
        const user = this.normalizeCrmCurrentUser(response && response.user);
        if (!response || !response.success || !user) {
          throw new Error((response && response.message) || 'Invalid CRM login response.');
        }

        this.setStoredCrmUser(user);
        this.clearCrmCaches();
        return user;
      })
    );
  }

  getStoredCrmUser(): CrmCurrentUser | null {
    try {
      const rawUser = window.localStorage.getItem(CRM_CURRENT_USER_STORAGE_KEY);
      if (!rawUser) {
        return null;
      }

      const user = this.normalizeCrmCurrentUser(JSON.parse(rawUser));
      if (!user) {
        this.clearStoredCrmUser();
      }

      return user;
    } catch (_) {
      this.clearStoredCrmUser();
      return null;
    }
  }

  setStoredCrmUser(user: CrmCurrentUser): void {
    try {
      window.localStorage.setItem(CRM_CURRENT_USER_STORAGE_KEY, JSON.stringify(user));
    } catch (_) {
      return;
    }
  }

  clearStoredCrmUser(): void {
    try {
      window.localStorage.removeItem(CRM_CURRENT_USER_STORAGE_KEY);
    } catch (_) {
      return;
    }
  }

  getCrmActorHeaders(): HttpHeaders {
    const user = this.getStoredCrmUser();
    return user ? new HttpHeaders({ 'X-CRM-Actor-Id': String(user.id) }) : new HttpHeaders();
  }

  clearCrmCaches(): void {
    this.dashboardSnapshot = null;
    this.dashboardRequest$ = null;
    this.clientDetailsSnapshots.clear();
    this.lcDetailsSnapshots.clear();
  }

  getAllClients(): Observable<CrmDashboardData> {
    return this.getDashboardData();
  }

  getClient(id: number | string): Observable<CrmClientResponse> {
    return this.http.get<CrmClientResponse>(`${this.getClientUrl}/${id}.json`).pipe(
      tap((response) => this.cacheDashboardClient(response.client))
    );
  }

  getClientDetails(id: number | string): Observable<CrmClientDetailsData> {
    return this.getClientDetailsData(id);
  }

  getClientDetailsByClient(id: number | string): Observable<CrmClientDetailsData> {
    return this.getClientDetailsByClientData(id);
  }

  getClientContacts(id: number | string): Observable<CrmClientContactsResponse> {
    return this.http.get<CrmClientContactsResponse>(`${this.getClientContactsUrl}/${id}.json`);
  }

  getClientNetworks(id: number | string): Observable<CrmClientNetworksResponse> {
    return this.http.get<CrmClientNetworksResponse>(`${this.getClientNetworksUrl}/${id}.json`);
  }

  getClientReferralOptions(id: number | string): Observable<CrmClientReferralOption[]> {
    return this.http.get<CrmClientReferralOptionsResponse>(`${this.getClientReferralOptionsUrl}/${id}.json`).pipe(
      map((response) => (response && response.options) || [])
    );
  }

  getClientContactOptions(id: number | string): Observable<CrmClientContactOption[]> {
    return this.http.get<CrmClientContactOptionsResponse>(`${this.getClientContactOptionsUrl}/${id}.json`).pipe(
      map((response) => (response && response.options) || [])
    );
  }

  getLcDetails(id: number | string): Observable<CrmLcDetailsData> {
    return this.getLcDetailsData(id);
  }

  createClient(data: CrmClientPayload): Observable<CrmClientMutationResponse> {
    return this.http.post<CrmClientMutationResponse>(this.createClientUrl, data, this.getCrmActorRequestOptions()).pipe(
      tap((response) => this.cacheDashboardClient(response.client))
    );
  }

  updateClient(id: number | string, data: CrmClientPayload): Observable<CrmClientMutationResponse> {
    return this.http.post<CrmClientMutationResponse>(`${this.updateClientUrl}/${id}.json`, data, this.getCrmActorRequestOptions()).pipe(
      tap((response) => this.cacheDashboardClient(response.client))
    );
  }

  deleteDropdownItem(dropdownName: keyof CrmDropdownOptions, item: string): Observable<CrmDropdownMutationResponse> {
    return this.http.post<CrmDropdownMutationResponse>(
      `${this.deleteDropdownItemUrl}.json`,
      { dropdown_name: dropdownName, item },
      this.getCrmActorRequestOptions()
    ).pipe(
      map((response) => this.normalizeDropdownMutationResponse(response)),
      tap((response) => this.cacheDropdownOptions(response.dropdowns))
    );
  }

  createClientContact(id: number | string, data: CrmClientPayload): Observable<CrmClientContactMutationResponse> {
    return this.http.post<CrmClientContactMutationResponse>(`${this.createClientContactUrl}/${id}.json`, data, this.getCrmActorRequestOptions());
  }

  createClientNote(id: number | string, data: CrmClientPayload): Observable<CrmClientLogMutationResponse> {
    return this.http.post<CrmClientLogMutationResponse>(`${this.createClientNoteUrl}/${id}.json`, data, this.getCrmActorRequestOptions());
  }

  updateClientNote(id: number | string, noteId: number | string, data: CrmClientPayload): Observable<CrmClientLogMutationResponse> {
    return this.http.post<CrmClientLogMutationResponse>(`${this.updateClientNoteUrl}/${id}/${noteId}.json`, data, this.getCrmActorRequestOptions());
  }

  createClientActivity(id: number | string, data: CrmClientPayload): Observable<CrmClientLogMutationResponse> {
    return this.http.post<CrmClientLogMutationResponse>(`${this.createClientActivityUrl}/${id}.json`, data, this.getCrmActorRequestOptions());
  }

  createClientAction(id: number | string, data: CrmClientPayload): Observable<CrmClientActionMutationResponse> {
    return this.http.post<CrmClientActionMutationResponse>(`${this.createClientActionUrl}/${id}.json`, data, this.getCrmActorRequestOptions()).pipe(
      tap((response) => this.cacheClientActions(id, response.actions))
    );
  }

  updateClientAction(id: number | string, actionId: number | string, data: CrmClientPayload): Observable<CrmClientActionMutationResponse> {
    return this.http.post<CrmClientActionMutationResponse>(`${this.updateClientActionUrl}/${id}/${actionId}.json`, data, this.getCrmActorRequestOptions()).pipe(
      tap((response) => this.cacheClientActions(id, response.actions))
    );
  }

  completeClientAction(id: number | string, actionId: number | string): Observable<CrmClientActionMutationResponse> {
    return this.http.post<CrmClientActionMutationResponse>(`${this.completeClientActionUrl}/${id}/${actionId}.json`, {}, this.getCrmActorRequestOptions()).pipe(
      tap((response) => this.cacheClientActions(id, response.actions))
    );
  }

  disregardClientAction(id: number | string, actionId: number | string): Observable<CrmClientActionMutationResponse> {
    return this.http.post<CrmClientActionMutationResponse>(`${this.disregardClientActionUrl}/${id}/${actionId}.json`, {}, this.getCrmActorRequestOptions()).pipe(
      tap((response) => this.cacheClientActions(id, response.actions))
    );
  }

  getDashboardData(refresh: boolean = false): Observable<CrmDashboardData> {
    if (!refresh && this.dashboardSnapshot) {
      return of(this.dashboardSnapshot);
    }

    if (this.dashboardRequest$) {
      return this.dashboardRequest$;
    }

    this.dashboardRequest$ = this.http.get<CrmDashboardData>(this.getAllClientsUrl).pipe(
      map((data) => this.normalizeDashboardData(data)),
      tap((data) => {
        this.dashboardSnapshot = data;
      }),
      shareReplay(1),
      finalize(() => {
        this.dashboardRequest$ = null;
      })
    );

    return this.dashboardRequest$;
  }

  getDashboardEnrichmentData(): Observable<Partial<CrmDashboardData>> {
    return this.http.get<Partial<CrmDashboardData>>(this.getAllClientsEnrichmentUrl).pipe(
      map((data) => this.normalizeDashboardEnrichmentData(data)),
      tap((data) => this.mergeDashboardEnrichmentData(data))
    );
  }

  getAirCarrierLicences(): Observable<CrmAirCarrierLicence[]> {
    return this.http.get<{ licences: CrmAirCarrierLicence[] }>(this.airCarrierLicencesUrl).pipe(
      map((data) => Array.isArray(data?.licences) ? data.licences : [])
    );
  }

  getDashboardOptionsData(): Observable<Partial<CrmDashboardData>> {
    return this.http.get<Partial<CrmDashboardData>>(this.getAllClientsOptionsUrl).pipe(
      map((data) => this.normalizeDashboardOptionsData(data)),
      tap((data) => this.mergeDashboardEnrichmentData(data))
    );
  }

  getDashboardRecordDetailsData(): Observable<Partial<CrmDashboardData>> {
    return this.http.get<Partial<CrmDashboardData>>(this.getAllClientsRecordDetailsUrl).pipe(
      map((data) => ({
        lcs: data.lcs || data.networks || []
      })),
      tap((data) => this.mergeDashboardRecordDetailsData(data))
    );
  }

  getDashboardActionsData(): Observable<Partial<CrmDashboardData>> {
    return this.http.get<Partial<CrmDashboardData>>(this.getAllClientsActionsUrl).pipe(
      map((data) => ({
        actionQueue: data.actionQueue || []
      })),
      tap((data) => this.mergeDashboardEnrichmentData(data))
    );
  }

  getDashboardVendorsData(): Observable<Partial<CrmDashboardData>> {
    return this.http.get<Partial<CrmDashboardData>>(this.getAllClientsVendorsUrl).pipe(
      map((data) => ({
        vendorLcSummary: data.vendorLcSummary || [],
        vendorLcOwners: data.vendorLcOwners || []
      })),
      tap((data) => this.mergeDashboardEnrichmentData(data))
    );
  }

  getClientDetailsData(id: number | string): Observable<CrmClientDetailsData> {
    return this.http.get<CrmClientDetailsData>(`${this.getClientDetailsUrl}/${id}.json`).pipe(
      map((data) => this.normalizeClientDetailsData(data)),
      tap((data) => this.cacheClientDetails(id, data))
    );
  }

  getClientDetailsEnrichmentData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsEnrichmentUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsLcData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsLcDataUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsMetricsData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsMetricsUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsUsersData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsUsersUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsVendorLcsData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsVendorLcsUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsByClientData(id: number | string): Observable<CrmClientDetailsData> {
    return this.http.get<CrmClientDetailsData>(`${this.getClientDetailsByClientUrl}/${id}.json`).pipe(
      map((data) => this.normalizeClientDetailsData(data)),
      tap((data) => this.cacheClientDetails(id, data))
    );
  }

  getClientDetailsByClientEnrichmentData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsByClientEnrichmentUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsByClientLcData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsByClientLcDataUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsByClientMetricsData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsByClientMetricsUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsByClientUsersData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsByClientUsersUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsByClientVendorLcsData(id: number | string): Observable<Partial<CrmClientDetailsData>> {
    return this.http.get<Partial<CrmClientDetailsData>>(`${this.getClientDetailsByClientVendorLcsUrl}/${id}.json`).pipe(
      tap((data) => this.mergeClientDetails(id, data))
    );
  }

  getClientDetailsSnapshot(id: number | string): CrmClientDetailsData {
    return this.clientDetailsSnapshots.get(String(id)) || ({
      clientId: String(id),
      clientName: `LC #${id}`,
      support: [],
      quickbooks: {
        customer: null,
        invoices: []
      },
      actions: [],
      networks: []
    } as unknown as CrmClientDetailsData);
  }

  getLcDetailsData(id: number | string): Observable<CrmLcDetailsData> {
    return this.http.get<CrmLcDetailsData>(`${this.getLcDetailsUrl}/${id}.json`).pipe(
      tap((data) => this.cacheLcDetails(id, data))
    );
  }

  getLcDetailsSnapshot(id: number | string): CrmLcDetailsData {
    return this.lcDetailsSnapshots.get(String(id)) || ({
      networkId: String(id),
      clientId: null,
      clientName: `LC #${id}`,
      linkId: null,
      isPrimary: true,
      crmStatus: null,
      testMode: false,
      testModeNote: null,
      notes: null,
      metrics: {
        networkID: Number(id) || 0,
        tokens_used: 0,
        tokens_total: 0,
        student_count: 0,
        module_draft_count: 0,
        module_published_count: 0,
        training_in_progress: 0
      },
      primaryContact: null,
      networkInfo: null,
      lcContacts: [],
      subscription: {
        subscriptionStart: null,
        subscriptionEnd: null,
        advancePay: false,
        invoiceRef: null,
        note: null
      },
      references: {
        invoiceReference: null,
        supportTicketReference: null,
        externalReferences: []
      },
      actions: [],
      communications: [],
      supportTickets: [],
      quickbooksInvoices: [],
      changelog: [],
      lcs: []
    } as unknown as CrmLcDetailsData);
  }

  getAvailablePrimaryNetworks(): Observable<CrmDashboardData['availablePrimaryNetworks']> {
    if (this.dashboardSnapshot) {
      const cachedOptions = this.dashboardSnapshot.availablePrimaryNetworks || [];
      if (cachedOptions.length > 0) {
        return of(cachedOptions);
      }

      return this.getDashboardOptionsData().pipe(map((data) => data.availablePrimaryNetworks || []));
    }

    return this.getDashboardOptionsData().pipe(map((data) => data.availablePrimaryNetworks || []));
  }

  getNextCompanyNetworkLinkId(): number {
    return Date.now();
  }

  appendClientNetworkLinks(companyId: number, links: CrmCompanyNetworkLink[]): Observable<CrmClientDetailsData> {
    return of(this.getClientDetailsSnapshot(companyId));
  }

  removeClientNetworkLinks(companyId: number, linkIds: number[]): Observable<CrmClientDetailsData> {
    return of(this.getClientDetailsSnapshot(companyId));
  }

  updateClientNetworkLink(
    companyId: number,
    linkId: number,
    updates: Partial<CrmCompanyNetworkLink>
  ): Observable<CrmClientDetailsData> {
    return of(this.getClientDetailsSnapshot(companyId));
  }

  updateActionItem(actionId: number, updates: Partial<CrmActionItem>): Observable<CrmActionItem | null> {
    const updated = this.updateCachedAction(actionId, updates);
    return of(updated);
  }

  completeActionItem(actionId: number): Observable<CrmActionItem | null> {
    return this.updateActionItem(actionId, { status: 'complete' });
  }

  addCommunicationMessage(threadId: number, author: string, body: string, attachmentNames: string[] = []): Observable<unknown> {
    return of({ threadId, author, body, attachmentNames });
  }

  linkSupportTicket(ticket: CrmSupportTicketPlaceholder): Observable<CrmSupportTicketPlaceholder> {
    return of(ticket);
  }

  linkQuickBooksInvoice(invoice: CrmQuickBooksInvoicePlaceholder): Observable<CrmQuickBooksInvoicePlaceholder> {
    return of(invoice);
  }

  searchDuplicateRecords(term: string): Observable<CrmDuplicateCandidate[]> {
    if (this.dashboardSnapshot) {
      return of(this.findDuplicateCandidates(term, this.dashboardSnapshot));
    }

    return this.getDashboardData().pipe(map((data) => this.findDuplicateCandidates(term, data)));
  }

  resetMockState(): Observable<CrmDashboardData> {
    this.clearCrmCaches();
    return this.getDashboardData(true);
  }

  private getCrmActorRequestOptions(): { headers: HttpHeaders } {
    return { headers: this.getCrmActorHeaders() };
  }

  private normalizeCrmCurrentUser(rawUser: any): CrmCurrentUser | null {
    if (!rawUser) {
      return null;
    }

    const id = Number(rawUser.id);
    if (!id || !isFinite(id)) {
      return null;
    }

    return {
      id,
      username: String(rawUser.username || ''),
      email: String(rawUser.email || ''),
      first_name: String(rawUser.first_name || ''),
      last_name: String(rawUser.last_name || '')
    };
  }

  private normalizeDashboardData(data: CrmDashboardData): CrmDashboardData {
    const lcs = data.lcs || data.networks || [];
    data.lcs = lcs;
    delete data.networks;
    data.unlinkedCompanies = data.unlinkedCompanies || [];
    data.availablePrimaryNetworks = data.availablePrimaryNetworks || [];
    data.dropdowns = this.normalizeDropdownOptions(data.dropdowns);
    data.actionQueue = data.actionQueue || [];
    data.vendorLcSummary = data.vendorLcSummary || [];
    data.vendorLcOwners = data.vendorLcOwners || [];
    data.tokenSummary = data.tokenSummary || {
      total: 0,
      used: 0,
      unused: 0,
      byRole: [],
      byLc: []
    };
    data.tokenSummary.byRole = data.tokenSummary.byRole || [];
    data.tokenSummary.byLc = (data.tokenSummary.byLc || []).map((lc) => ({
      ...lc,
      roles: lc.roles || [],
      requestHistory: lc.requestHistory || []
    }));
    data.supportSummary = data.supportSummary || [];
    data.quickbooksSummary = data.quickbooksSummary || [];
    data.marketingSummary = data.marketingSummary || [];
    data.adminSummary = data.adminSummary || [];

    return data;
  }

  private normalizeDashboardEnrichmentData(data: Partial<CrmDashboardData>): Partial<CrmDashboardData> {
    data.tokenSummary = data.tokenSummary || {
      total: 0,
      used: 0,
      unused: 0,
      byRole: [],
      byLc: []
    };
    data.tokenSummary.byRole = data.tokenSummary.byRole || [];
    data.tokenSummary.byLc = (data.tokenSummary.byLc || []).map((lc) => ({
      ...lc,
      roles: lc.roles || [],
      requestHistory: lc.requestHistory || []
    }));

    return data;
  }

  private normalizeDashboardOptionsData(data: Partial<CrmDashboardData>): Partial<CrmDashboardData> {
    return {
      availablePrimaryNetworks: data.availablePrimaryNetworks || [],
      dropdowns: this.normalizeDropdownOptions(data.dropdowns)
    };
  }

  private normalizeDropdownMutationResponse(response: CrmDropdownMutationResponse): CrmDropdownMutationResponse {
    return {
      success: !!(response && response.success),
      message: response && response.message ? response.message : '',
      dropdowns: this.normalizeDropdownOptions(response ? response.dropdowns : null)
    };
  }

  private cacheDropdownOptions(dropdowns: CrmDropdownOptions): void {
    const normalizedDropdowns = this.normalizeDropdownOptions(dropdowns);
    if (this.dashboardSnapshot) {
      this.dashboardSnapshot = {
        ...this.dashboardSnapshot,
        dropdowns: normalizedDropdowns
      };
    }

    this.clientDetailsSnapshots.forEach((snapshot, key) => {
      this.clientDetailsSnapshots.set(key, {
        ...snapshot,
        dropdowns: normalizedDropdowns
      });
    });
  }

  private mergeDashboardEnrichmentData(data: Partial<CrmDashboardData>): void {
    if (!this.dashboardSnapshot) {
      return;
    }

    this.dashboardSnapshot = {
      ...this.dashboardSnapshot,
      ...data,
      tokenSummary: data.tokenSummary || this.dashboardSnapshot.tokenSummary
    };
  }

  private mergeDashboardRecordDetailsData(data: Partial<CrmDashboardData>): void {
    if (!this.dashboardSnapshot) {
      return;
    }

    const details = data.lcs || data.networks || [];
    if (details.length === 0) {
      return;
    }

    const detailRows = new Map(details.map((row) => [this.dashboardRowKey(row), row]));
    this.dashboardSnapshot = {
      ...this.dashboardSnapshot,
      lcs: (this.dashboardSnapshot.lcs || []).map((row) => ({
        ...row,
        ...(detailRows.get(this.dashboardRowKey(row)) || {})
      }))
    };
  }

  private dashboardRowKey(row: CrmNetwork): string {
    const networkId = Number(row.networkID || row.network_id || 0);
    if (networkId > 0) {
      return `network:${networkId}`;
    }

    return `company:${Number(row.company_id || row.id || 0)}`;
  }

  private normalizeDropdownOptions(dropdowns?: Partial<CrmDropdownOptions> | null): CrmDropdownOptions {
    const source = dropdowns || EMPTY_CRM_DROPDOWNS;
    return {
      source: this.normalizeDropdownItems(source.source),
      industry: this.normalizeDropdownItems(source.industry),
      client_tags: this.normalizeDropdownItems(source.client_tags),
      user_tags: this.normalizeDropdownItems(source.user_tags)
    };
  }

  private normalizeClientDetailsData(data: CrmClientDetailsData): CrmClientDetailsData {
    return {
      ...data,
      dropdowns: this.normalizeDropdownOptions(data.dropdowns)
    };
  }

  private normalizeDropdownItems(items?: string[] | null): string[] {
    if (!Array.isArray(items)) {
      return [];
    }

    const seen = new Set<string>();
    return items
      .map((item) => String(item || '').trim())
      .filter((item) => {
        const key = item.toLowerCase();
        if (!item || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }

  private cacheDashboardClient(client: CrmNetwork | null): void {
    if (!client || !this.dashboardSnapshot) {
      return;
    }

    const rows = this.dashboardSnapshot.lcs || this.dashboardSnapshot.networks || [];
    const clientCompanyId = Number(client.company_id || client.id || 0);
    let didMerge = false;
    rows.forEach((row, index) => {
      const rowCompanyId = Number(row.company_id || (!row.has_lc ? row.id : 0));
      const isSameNetwork = !!client.networkID && row.networkID === client.networkID;
      const isSameCompany = clientCompanyId > 0 && rowCompanyId === clientCompanyId;
      if (!isSameNetwork && !isSameCompany) {
        return;
      }

      rows[index] = {
        ...row,
        ...client,
        id: row.id,
        networkID: row.networkID,
        network_id: row.network_id,
        lc_name: row.lc_name,
        name: row.has_lc ? row.name : client.name,
        has_lc: row.has_lc,
        primary_network: row.has_lc ? row.primary_network : client.primary_network,
        linked_networks: row.has_lc ? row.linked_networks : client.linked_networks
      };
      didMerge = true;
    });

    if (!didMerge) {
      rows.push(client);
    }
    this.dashboardSnapshot.lcs = rows;
  }

  private cacheClientDetails(id: number | string, data: CrmClientDetailsData): void {
    const keys = [String(id), String(data.clientId || ''), String(data.networkId || ''), String(data.companyId || '')].filter(Boolean);
    keys.forEach((key) => this.clientDetailsSnapshots.set(key, data));
  }

  private mergeClientDetails(id: number | string, data: Partial<CrmClientDetailsData>): void {
    const lookupKeys = [String(id), String(data.clientId || ''), String(data.networkId || ''), String(data.companyId || '')].filter(Boolean);
    let existing: CrmClientDetailsData | null = null;
    lookupKeys.some((key) => {
      existing = this.clientDetailsSnapshots.get(key) || null;
      return !!existing;
    });

    if (!existing) {
      return;
    }

    const networks = Array.isArray(data.networks)
      ? this.mergeClientDetailNetworks(existing.networks || [], data.networks)
      : existing.networks;

    this.cacheClientDetails(id, {
      ...existing,
      ...data,
      networks
    } as CrmClientDetailsData);
  }

  private mergeClientDetailNetworks(currentNetworks: CrmClientNetwork[], incomingNetworks: CrmClientNetwork[]): CrmClientNetwork[] {
    const networkMap = new Map<string, CrmClientNetwork>();

    currentNetworks.forEach((network) => {
      networkMap.set(this.clientDetailNetworkKey(network), network);
    });

    incomingNetworks.forEach((network) => {
      const key = this.clientDetailNetworkKey(network);
      const existing = networkMap.get(key);
      networkMap.set(key, existing ? { ...existing, ...network } : network);
    });

    return Array.from(networkMap.values());
  }

  private clientDetailNetworkKey(network: CrmClientNetwork): string {
    const legacyNetwork = network as CrmClientNetwork & { networkID?: number };
    const networkId = Number(network.network_id || legacyNetwork.networkID || 0);
    if (networkId > 0) {
      return `network:${networkId}`;
    }

    return `link:${network.id || network.client_name || network.network_name || 'prospect'}`;
  }

  private cacheClientActions(id: number | string, actions: CrmActionItem[] | null | undefined): void {
    if (!Array.isArray(actions)) {
      return;
    }

    const key = String(id);
    const snapshot = this.clientDetailsSnapshots.get(key);
    if (snapshot) {
      snapshot.actions = actions;
    }
    this.dashboardSnapshot = null;
  }

  private cacheLcDetails(id: number | string, data: CrmLcDetailsData): void {
    const keys = [String(id), String(data.networkId || '')].filter(Boolean);
    keys.forEach((key) => this.lcDetailsSnapshots.set(key, data));
  }

  private updateCachedAction(actionId: number, updates: Partial<CrmActionItem>): CrmActionItem | null {
    let updatedAction: CrmActionItem | null = null;
    this.clientDetailsSnapshots.forEach((snapshot) => {
      snapshot.actions = (snapshot.actions || []).map((action) => {
        if (action.id !== actionId) {
          return action;
        }
        updatedAction = { ...action, ...updates };
        return updatedAction;
      });
    });

    return updatedAction;
  }

  private findDuplicateCandidates(term: string, data: CrmDashboardData): CrmDuplicateCandidate[] {
    const normalized = this.normalizeSearchTerm(term);
    if (!normalized) {
      return [];
    }

    return (data.lcs || data.networks || [])
      .filter((row) => {
        return [
          row.name,
          row.legal_name,
          row.company_name,
          row.lc_name,
          row.email_general
        ].some((value) => this.normalizeSearchTerm(value || '').includes(normalized));
      })
      .slice(0, 10)
      .map((row) => ({
        id: row.id,
        label: row.company_name || row.name,
        normalizedKey: this.normalizeSearchTerm(row.company_name || row.name),
        reason: row.vendor ? 'Vendor LC' : 'Existing LC'
      }));
  }

  private normalizeSearchTerm(value: string): string {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }
}

let crmServiceStub: Partial<CrmService> = {};
export { crmServiceStub };
